import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, Zap, MessageCircle, ArrowRight, ChevronDown,
  Check, Star, Crown, Sun, Globe, Heart, Shield, Eye,
  Brain, Target, Users, Clock, Compass
} from 'lucide-react';

const HERO_IMG = "https://customer-assets.emergentagent.com/job_bb69c14e-e721-4f53-aed8-ca0b062ed855/artifacts/5rpwuvq2_ChatGPT%20Image%20Apr%2024%2C%202026%2C%2004_40_51%20PM.png";
const WA_LINK = "https://wa.me/601171383699";

const CTAButton = ({ text = "ENQUIRE ON WHATSAPP", className = "" }) => (
  <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
    className={`inline-flex items-center gap-3 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#FCD34D] hover:to-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-lg transition-all duration-300 hover:shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)] hover:scale-[1.02] active:scale-[0.98] px-10 py-5 text-base ${className}`}>
    <MessageCircle className="w-5 h-5" />
    <span>{text}</span>
    <ArrowRight className="w-5 h-5" />
  </a>
);

const AnimatedSection = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} id={id} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </section>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#D4AF37]/20">
      <button className="w-full flex items-center justify-between py-5 text-left group" onClick={() => setOpen(!open)}>
        <span className="text-white font-medium pr-4 group-hover:text-[#D4AF37] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}>
        <p className="text-gray-400 leading-relaxed">{a}</p>
      </div>
    </div>
  );
};

const realms = [
  { char: "神", label: "DIVINE", desc: "Connect with spiritual intelligence and cosmic purpose" },
  { char: "天", label: "HEAVEN", desc: "Align with celestial timing and heavenly opportunities" },
  { char: "地", label: "EARTH", desc: "Ground your energy and harmonize your environment" },
  { char: "人", label: "HUMAN", desc: "Master your mindset, actions, and relationships" },
];

const benefits = [
  { icon: Crown, text: "Achieve career and business breakthroughs through aligning spirit and mind" },
  { icon: Target, text: "Become a magnet for career, wealth, and relationship success" },
  { icon: Users, text: "Attract and gain favour from people in high positions — bosses, investors, partners" },
  { icon: Brain, text: "Manifest solutions for difficult or complex problems" },
  { icon: Heart, text: "Attain more peace and confidence about the future" },
  { icon: Compass, text: "Command your intention, command your results" },
];

const learnings = [
  "The ancient Qimen Dunjia system and how it maps to the 4 Realms — Divine, Heaven, Earth, Human",
  "How to identify your personal Qimen deity and activate your hidden superpower",
  "The daily attunement framework to align your energy with the universe each morning",
  "The science behind why Qimen works — mathematical, not mystical — so you practice with full confidence",
  "How combining BaZi (know yourself) + Qimen (align energy) + Feng Shui (optimize space) creates exponential results",
  "Real-world application for wealth, career decisions, relationships, and health",
  "The tools, knowledge & guidance towards mastering Qimen manifestation",
];

const testimonials = [
  { name: "Thomas T.", text: "In an instant I connected with the way he sees life. Chinese metaphysics, combining ancient wisdom and modern success — it was just awesome." },
  { name: "Shin W.", text: "Started getting better prices from my suppliers and closing million dollar deals after applying the attunement principles." },
  { name: "Allen", text: "My business slumped into million dollars in debt. After applying the manifestation techniques, I got a huge increase within 6 weeks." },
];

const faqs = [
  { q: "Do I need any prior knowledge of Qimen Dunjia?", a: "No. Master Steven will guide you from the fundamentals. This program is designed for complete beginners and experienced practitioners alike." },
  { q: "What is the difference between Qimen Attunement and Qimen Forecasting?", a: "Attunement focuses on manifestation — aligning your energy with the 4 Realms to create outcomes. Forecasting focuses on strategic decision-making and timing. They complement each other powerfully." },
  { q: "How are sessions conducted?", a: "All sessions are conducted live via Zoom. You will have regular scheduled sessions with Master Steven Yong, plus full access to replays." },
  { q: "Is this religious or spiritual?", a: "Qimen Dunjia is a mathematical and strategic system rooted in Chinese metaphysics. It is not tied to any religion. People of all backgrounds practice it." },
  { q: "What results can I expect?", a: "Results vary by individual, but students commonly report breakthroughs in career, business decisions, relationships, and a deeper sense of clarity and confidence within weeks of practice." },
];

const QimenAttunementPage = () => {
  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex flex-col items-center" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0800] via-[#080808] to-[#080808]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[150px]" />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-24 text-center">
          <div className="inline-flex items-center gap-2 mb-8 px-5 py-2.5 bg-[#D4AF37]/10 backdrop-blur-sm rounded-full border border-[#D4AF37]/25">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold">Qimen Attunement (Manifestation)</span>
          </div>

          <img
            alt="Qimen Attunement - Align with the 4 Realms"
            className="w-full max-w-4xl mx-auto rounded-2xl shadow-[0_20px_80px_-20px_rgba(212,175,55,0.25)] border border-[#D4AF37]/10"
            src={HERO_IMG}
            data-testid="hero-img"
          />

          <p className="mt-8 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Align with the 4 Realms to manifest with power, timing & clarity. A transformational program with{' '}
            <span className="text-[#D4AF37] font-semibold">Master Steven Yong</span>.
          </p>
          <p className="mt-4 text-[#D4AF37] font-semibold text-lg">Not Wishing. Attuning.</p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <CTAButton />
            <a href="#pricing" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#FCD34D] font-semibold transition-colors py-4">
              <span>See Pricing</span><ChevronDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== THE 4 REALMS ===== */}
      <AnimatedSection className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold block mb-4">The Foundation</span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            The <span className="text-[#D4AF37]">4 Realms</span> of Manifestation
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-16">
            When the Divine, Heaven, Earth and Human align, manifestation becomes natural.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {realms.map((r, i) => (
              <div key={i} className="group p-6 bg-white/[0.02] border border-[#D4AF37]/15 rounded-2xl hover:border-[#D4AF37]/40 transition-all duration-300">
                <div className="text-5xl mb-3 text-[#D4AF37] font-serif group-hover:scale-110 transition-transform">{r.char}</div>
                <p className="text-xs text-[#D4AF37] uppercase tracking-widest font-bold mb-2">{r.label}</p>
                <p className="text-sm text-gray-400">{r.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-gray-300 max-w-lg mx-auto italic">
            "You don't chase outcomes. You become aligned with them."
          </p>
        </div>
      </AnimatedSection>

      {/* ===== BENEFITS ===== */}
      <AnimatedSection className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold block mb-4">What You Will Achieve</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              Command Your Intention,{' '}<span className="text-[#D4AF37]">Command Your Results</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {benefits.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-start gap-4 p-5 bg-[#D4AF37]/5 border border-[#D4AF37]/10 rounded-xl hover:border-[#D4AF37]/25 transition-all">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <span className="text-white leading-relaxed">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== TESTIMONIALS ===== */}
      <AnimatedSection className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">
            This Sounded Too Good To Be True…{' '}<span className="text-[#D4AF37]">But They Are Now Believers</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 bg-white/[0.02] border border-[#D4AF37]/15 rounded-xl">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />)}
                </div>
                <p className="text-gray-300 italic mb-4 text-sm leading-relaxed">"{t.text}"</p>
                <p className="text-[#D4AF37] font-semibold text-sm">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== WHAT IS QIMEN ===== */}
      <AnimatedSection className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">
            What Is <span className="text-[#D4AF37]">Qimen Intelligence?</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Ancient Wisdom", desc: "3,000 years ago, Emperors and Generals used Qimen to win wars. They didn't rely on luck. They relied on timing, positioning, and strategic alignment." },
              { title: "Modern Intelligence", desc: "Adapted for today, it becomes an almost omniscient 'Qimen Intelligence' that helps you make sharper decisions, move at the right time, and align your energy with opportunity." },
              { title: "Not Magic", desc: "This isn't about crystals or chanting. It's about understanding the forces that affect your career, business, and relationships — and learning to work with them to unlock your TRUE POTENTIAL." },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-white/[0.02] border border-white/[0.06] rounded-xl hover:border-[#D4AF37]/20 transition-all">
                <h3 className="text-[#D4AF37] font-bold mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== WHAT YOU WILL LEARN ===== */}
      <AnimatedSection className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold block mb-4">Curriculum</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              What You Will <span className="text-[#D4AF37]">Learn</span>
            </h2>
          </div>
          <div className="space-y-4">
            {learnings.map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:border-[#D4AF37]/20 transition-all">
                <div className="shrink-0 w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mt-0.5">
                  <span className="text-[#D4AF37] text-sm font-bold">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <span className="text-white leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ===== PRICING ===== */}
      <AnimatedSection id="pricing" className="py-24 px-6 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-semibold block mb-4">Investment</span>
            <h2 className="text-3xl sm:text-4xl font-bold">
              Transform Your <span className="text-[#D4AF37]">Manifestation Power</span>
            </h2>
          </div>
          <div className="max-w-lg mx-auto relative" data-testid="pricing-card">
            <div className="absolute inset-0 bg-[#D4AF37]/10 blur-3xl rounded-3xl" />
            <div className="relative bg-[#0a0a0a] border-2 border-[#D4AF37]/40 rounded-2xl p-8 sm:p-10 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.3)]">
              <div className="text-center">
                <p className="text-[#D4AF37] text-sm uppercase tracking-widest font-semibold mb-6">Qimen Attunement Mastery</p>
                <div className="mb-6">
                  <span className="text-5xl sm:text-6xl font-bold text-white">USD 3,000</span>
                </div>
                <div className="h-px w-full bg-white/10 mb-6" />
                <div className="space-y-4 text-left mb-8">
                  {[
                    "Live coaching with Master Steven Yong",
                    "Personal Qimen chart analysis",
                    "4 Realms attunement framework",
                    "Daily manifestation techniques",
                    "Wealth & career activation strategies",
                    "Lifetime access to session replays",
                    "Direct WhatsApp support",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white">{item}</span>
                    </div>
                  ))}
                </div>
                <CTAButton className="w-full justify-center" />
                <p className="text-xs text-gray-500 mt-4">Limited spots available. Speak with us to reserve yours.</p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FAQ ===== */}
      <AnimatedSection id="faq" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center">
            Common <span className="text-[#D4AF37]">Questions</span>
          </h2>
          <div>{faqs.map((item, i) => <FaqItem key={i} q={item.q} a={item.a} />)}</div>
        </div>
      </AnimatedSection>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 px-6 relative" data-testid="final-cta">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0800] via-[#080808] to-[#080808]" />
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Stop Wishing.{' '}<span className="text-[#D4AF37]">Start Attuning.</span>
          </h2>
          <p className="text-gray-400 max-w-lg mx-auto mb-4 leading-relaxed">
            When the Divine, Heaven, Earth and Human align, manifestation becomes natural. Are you ready to align?
          </p>
          <p className="text-[#D4AF37] font-semibold mb-10 text-lg">
            神 · 天 · 地 · 人
          </p>
          <CTAButton />
          <p className="text-sm text-gray-500 mt-6">Qimen Attunement Mastery · Master Steven Yong</p>
        </div>
      </section>
    </div>
  );
};

export default QimenAttunementPage;
